import { Dialog, DialogContent } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  AdditionalEvidenceRequest,
  mapOtherEvidenceToRequest,
  mapStatementOfAssuranceToRequest,
  StatementsOfAssuranceRequest,
} from "../../../../../domain/AdditionalEvidenceRequest";
import { Attachment } from "../../../../../domain/Attachment";
import {
  deleteEvidenceAttachment,
  loadingEvidenceRequest,
  submitEvidence,
  updatingEvidence,
  updatingOtherEvidence,
} from "../../../../../redux/actions";
import {
  authSelector,
  evidenceStateSelector,
} from "../../../../../redux/selectors";
import { expansionApplicationEvidenceService } from "../../../../../services";
import { AdditionalEvidenceBody } from "../../ExpansionActivityFeedTypes";
import { ActivityDialogProps } from "../DialogProps";
import { EvidenceDeclaration } from "./EvidenceDeclaration";
import { EvidenceSummary } from "./EvidenceSummary";
import { OtherEvidence } from "./OtherEvidence";

export const EvidenceDialog: React.FC<ActivityDialogProps> = ({
  onClose,
  activity,
}) => {
  const dispatch = useDispatch();
  const { token } = useSelector(authSelector);

  const [focussedEntity, setFocussedEntity] = useState<
    StatementsOfAssuranceRequest | "other" | null
  >(null);

  const { itemId, id } = useParams<{
    itemId: string;
    id: string;
  }>();

  const body: AdditionalEvidenceBody = JSON.parse(activity.activityMetaData);

  useEffect(() => {
    dispatch(
      loadingEvidenceRequest(itemId, id, body.AdditionalEvidenceRequestId)
    );
  }, [dispatch, id, itemId, body.AdditionalEvidenceRequestId]);

  const state = useSelector(evidenceStateSelector);

  const saveSao = (data: StatementsOfAssuranceRequest) => {
    if (state.request) {
      const mappedRequest = mapStatementOfAssuranceToRequest(
        data,
        state.request
      );
      dispatch(updatingEvidence(itemId, id, state.request.id, mappedRequest));
    }
    setFocussedEntity(null);
  };

  const saveOtherEvidence = (data: {
    files: File[];
    otherEvidenceResponseText: string;
  }) => {
    if (state.request) {
      const mappedRequest = mapOtherEvidenceToRequest(
        data.otherEvidenceResponseText,
        state.request
      );
      dispatch(
        updatingOtherEvidence(
          itemId,
          id,
          state.request.id,
          data.files,
          mappedRequest
        )
      );
    }
    setFocussedEntity(null);
  };

  const submit = () => {
    if (state.request) {
      dispatch(
        submitEvidence(itemId, id, state.request.id, state.request.timestamp)
      );
    }
    onClose();
  };

  const deleteAttachment = (attachment: Attachment) =>
    dispatch(deleteEvidenceAttachment(attachment));

  const downloadAttachment = (blobId: string): Promise<Blob> =>
    expansionApplicationEvidenceService.downloadAttachment(
      itemId,
      id,
      state.request!.id,
      blobId,
      { token }
    );

  const getContentRenderer = (request: AdditionalEvidenceRequest) => {
    const statementOfAssurance = focussedEntity as StatementsOfAssuranceRequest;
    const otherEvidence = focussedEntity as "other";

    if (statementOfAssurance && statementOfAssurance.declaration) {
      return (
        <EvidenceDeclaration
          statement={statementOfAssurance}
          onClick={(renderer) => setFocussedEntity(renderer)}
          onSave={saveSao}
        />
      );
    } else if (otherEvidence) {
      return (
        <OtherEvidence
          request={request}
          onClick={(renderer) => setFocussedEntity(renderer)}
          onSave={saveOtherEvidence}
          onDownload={downloadAttachment}
          onDeleteAttachment={deleteAttachment}
        />
      );
    } else {
      return (
        <EvidenceSummary
          request={request}
          onClick={(renderer) => setFocussedEntity(renderer)}
          onClose={onClose}
          onSubmit={submit}
          onDownload={downloadAttachment}
        />
      );
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="additional-evidence-dialog"
    >
      {state && state.status !== "UPDATING" && state.request ? (
        <>{getContentRenderer(state.request)}</>
      ) : (
        <>
          <DialogContent>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};
